/**
 * Client for the regex performance probe (VE-24948 / U5, ADR-001 as amended).
 *
 * THIS IS THE REGEX SAFETY GATE. `validateRegexEntry` keeps only the rules that
 * are certain from syntax alone; everything that used to be inferred about a
 * pattern's cost is now measured here. Review gates #1-#3 each broke an
 * inference-based screen — the last accepted `(\w)*` x10 at 133,679 ms on a
 * 40-character input while rejecting `(?:ab)+` — so the decision moved from
 * "this shape looks safe" to "this pattern was timed at page scale".
 *
 * FAIL CLOSED. Every path that is not a measurement comfortably inside
 * `REGEX_PROBE_BUDGET_MS` returns `ok: false`: the deadline elapsing, the worker
 * erroring, the worker failing to construct. A probe that cannot run is not a
 * pass — NFR-S4's ordering applies here as much as to a mutation.
 *
 * WHY THE FACTORY IS INJECTED. Same reason as `lexiconMatchWorkerClient`:
 * `new Worker(new URL(...))` does not work under jsdom and this repo's jest
 * moduleNameMapper only intercepts `worker-loader`. Injecting it is what makes
 * the deadline behaviour assertable with a fake worker and no pathological
 * pattern — a test that had to actually hang for two seconds would be a test
 * nobody runs.
 *
 * NFR-S2 / SEC-R3: the pattern never reaches a log, and `RegexProbeOutcome`
 * carries a duration, never the term.
 */

import { REGEX_PROBE_BUDGET_MS, REGEX_PROBE_DEADLINE_MS } from './limits';
import { buildProbeCorpus } from './regexProbeCorpus';
import { RegexProbeRequest, RegexProbeResponse } from './regexProbeWorker';

/** Minimal worker surface, so a fake is trivial to write. */
export interface ProbeWorkerLike {
  postMessage(message: RegexProbeRequest): void;
  addEventListener(
    type: 'message',
    listener: (event: MessageEvent<RegexProbeResponse>) => void
  ): void;
  addEventListener(type: 'error', listener: (event: unknown) => void): void;
  terminate(): void;
}

export type ProbeWorkerFactory = () => ProbeWorkerLike;

const defaultFactory: ProbeWorkerFactory = () =>
  new Worker(new URL('./regexProbeWorker.ts', import.meta.url), {
    type: 'module',
  }) as unknown as ProbeWorkerLike;

/** Why a probe refused a pattern. Drives which message the author is shown. */
export type RegexProbeFailure =
  /** Measured, and over `REGEX_PROBE_BUDGET_MS`. `elapsedMs` is populated. */
  | 'too-slow'
  /** The deadline elapsed with no answer — the worker was terminated. */
  | 'deadline'
  /** The pattern threw, or the worker could not be created. */
  | 'error';

export type RegexProbeOutcome =
  | { readonly ok: true; readonly elapsedMs: number }
  | {
      readonly ok: false;
      readonly failure: RegexProbeFailure;
      /** Populated only for `too-slow`; null when nothing was measured. */
      readonly elapsedMs: number | null;
    };

/**
 * Times `pattern` against page-scale adversarial inputs in a disposable worker.
 *
 * Resolves — it never rejects, because every caller's correct response to a
 * failure is the same as to a slow result: refuse the save. A throwing gate
 * would invite a `catch` that swallows into a pass.
 *
 * The worker is terminated on every path, including success: it is disposable
 * by design, and keeping one alive would give the next probe a warm JIT and a
 * measurement that flatters the pattern.
 */
export const probeRegexPerformance = (
  pattern: string,
  factory: ProbeWorkerFactory = defaultFactory
): Promise<RegexProbeOutcome> => {
  let worker: ProbeWorkerLike;
  try {
    worker = factory();
  } catch {
    return Promise.resolve({ ok: false, failure: 'error', elapsedMs: null });
  }

  return new Promise<RegexProbeOutcome>((resolve) => {
    let settled = false;
    const finish = (outcome: RegexProbeOutcome): void => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(deadline);
      try {
        worker.terminate();
      } catch {
        // A worker that cannot be terminated is already gone; the outcome
        // stands either way.
      }
      resolve(outcome);
    };

    const deadline = window.setTimeout(() => {
      // The pattern is stuck inside a single `exec`. Terminating is the whole
      // reason this runs in a worker (ADR-002).
      finish({ ok: false, failure: 'deadline', elapsedMs: null });
    }, REGEX_PROBE_DEADLINE_MS);

    worker.addEventListener('message', (event) => {
      const response = event.data;
      if (response.kind === 'error') {
        finish({ ok: false, failure: 'error', elapsedMs: null });
        return;
      }
      finish(
        response.elapsedMs <= REGEX_PROBE_BUDGET_MS
          ? { ok: true, elapsedMs: response.elapsedMs }
          : {
              ok: false,
              failure: 'too-slow',
              elapsedMs: response.elapsedMs,
            }
      );
    });

    worker.addEventListener('error', () => {
      finish({ ok: false, failure: 'error', elapsedMs: null });
    });

    try {
      worker.postMessage({ pattern, inputs: buildProbeCorpus(pattern) });
    } catch {
      finish({ ok: false, failure: 'error', elapsedMs: null });
    }
  });
};
